import { ContributionLegend, GitHubContributionGraph } from '@/components/changelog/github-contribution-graph'
import { Logo } from '@/components/logo'
import { Card } from '@/components/ui/card'
import { publicChangelogRepositoryPath } from '@/routes'
import type { PublicChangelogsOwnerShow } from '@/types'
import { Head, Link } from '@inertiajs/react'

type Props = PublicChangelogsOwnerShow

function GitHubMark() {
  return (
    <svg viewBox="0 0 16 16" className="size-5 fill-current" aria-hidden>
      <path d="M8 0C3.58 0 0 3.58 0 8c0 3.54 2.29 6.53 5.47 7.59.4.07.55-.17.55-.38 0-.19-.01-.82-.01-1.49-2.01.37-2.53-.49-2.69-.94-.09-.23-.48-.94-.82-1.13-.28-.15-.68-.52-.01-.53.63-.01 1.08.58 1.23.82.72 1.21 1.87.87 2.33.66.07-.52.28-.87.51-1.07-1.78-.2-3.64-.89-3.64-3.95 0-.87.31-1.59.82-2.15-.08-.2-.36-1.02.08-2.12 0 0 .67-.21 2.2.82.64-.18 1.32-.27 2-.27.68 0 1.36.09 2 .27 1.53-1.04 2.2-.82 2.2-.82.44 1.1.16 1.92.08 2.12.51.56.82 1.27.82 2.15 0 3.07-1.87 3.75-3.65 3.95.29.25.54.73.54 1.48 0 1.07-.01 1.93-.01 2.2 0 .21.15.46.55.38A8.013 8.013 0 0 0 16 8c0-4.42-3.58-8-8-8z" />
    </svg>
  )
}

export default function PublicChangelogsOwnerShow({ owner, publicRepos, totalChangesInLastWeek }: Props) {
  const description = `Public Git Digest changelog for ${owner}: ${totalChangesInLastWeek.toLocaleString()} code changes in the last 7 days across public repositories. Team commit history, not a repo-to-LLM dump.`

  return (
    <>
      <Head title={owner}>
        <meta head-key="description" name="description" content={description} />
      </Head>
      <div className="bg-background min-h-screen">
        <div className="mx-auto max-w-6xl px-4 py-10 sm:px-6 lg:px-8">
          <div className="mb-8 flex flex-col gap-4">
            <div className="flex items-center justify-between">
              <div className="text-muted-foreground flex items-center gap-2 text-sm sm:text-base">
                <GitHubMark />
                <h1 className="text-foreground text-lg font-semibold sm:text-xl">{owner} public changelog</h1>
              </div>
            </div>
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-muted-foreground text-sm sm:text-base">
                <span className="text-foreground font-semibold">{totalChangesInLastWeek.toLocaleString()}</span> code
                changes in the last 7 days.
              </p>
              <ContributionLegend />
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
            {publicRepos.map((repo) => (
              <Link key={repo.id} href={publicChangelogRepositoryPath(repo.owner, repo.name)} className="group h-full">
                <Card className="border-border/70 bg-card/80 group-hover:border-primary/60 h-full border p-4 shadow-sm transition-colors">
                  <div className="mb-4 flex items-center gap-2 text-sm font-semibold sm:text-base">
                    <GitHubMark />
                    <span className="truncate">{repo.name}</span>
                  </div>
                  <GitHubContributionGraph days={repo.contributions} startDayOfWeek={repo.startDayOfWeek} />
                </Card>
              </Link>
            ))}
          </div>

          <div className="mt-8 flex items-center justify-end gap-1.5">
            <span className="text-muted-foreground text-xs">Powered by</span>
            <Link href="/" className="transition-opacity hover:opacity-80">
              <Logo className="text-sm" />
            </Link>
          </div>
        </div>
      </div>
    </>
  )
}
